import { Avatar, AvatarFallback } from './ui/avatar';
import { Settings, User, LogOut, Gift, MessageCircle, Crown, UserCircle, Heart, Shield, Star, TrendingUp, Moon, Lock, ShieldCheck, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { useAuthContext } from '../contexts/AuthContext';
import { useUserDisplay } from '../hooks/useUserDisplay';
import { useTheme, themes } from './ThemeProvider';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { fetchSettings } from '../store/slices/settingsSlice';
import { useEffect } from 'react';

interface MobileSidebarContentProps {
  onNavigate: (screen: any) => void;
  onLogout: () => void;
  onSettings: () => void;
  onNotifications: () => void;
  onProfile: () => void;
  onThemeSelect: () => void;
  onClose?: () => void;
}

export default function MobileSidebarContent({
  onNavigate,
  onLogout,
  onSettings,
  onNotifications,
  onProfile,
  onThemeSelect,
  onClose
}: MobileSidebarContentProps) {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const { user } = useAuthContext();
  const { getProfilePhoto, getFullName, getInitials } = useUserDisplay();
  const { settings } = useAppSelector((state) => state.settings);
  const dispatch = useAppDispatch();
  
  // Debug logging
  console.log('🔧 [MobileSidebar] Render check:', {
    isNativeApp: (window as any).isNativeApp,
    smsInviteEnabled: settings?.features?.smsInviteEnabled,
    shouldShowButton: (window as any).isNativeApp && settings?.features?.smsInviteEnabled === true
  });
  
  const handleAction = (action: () => void) => {
    action();
    if (onClose) onClose();
  };

  const isAdmin = user?.role === 'admin';
  const isMarketing = user?.role === 'marketing' || user?.role === 'marketing_user';
  const isMarketingManager = user?.role === 'marketing_manager';
  const profilePhoto = getProfilePhoto();

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  return (
    <div className="flex flex-col h-full bg-white border-r">
      <div className={`p-6 bg-gradient-to-r ${currentTheme.primary} text-white`}>
        <div className="flex items-center gap-3">
          {profilePhoto ? (
            <div className="size-16 rounded-full ring-2 ring-white/30 overflow-hidden">
              <ImageWithFallback
                src={profilePhoto}
                alt={getFullName()}
                className="size-full object-cover"
              />
            </div>
          ) : (
            <Avatar className="size-16 ring-2 ring-white/30">
              <AvatarFallback className="bg-white/20 text-white text-xl">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
          )}
          <div>
            <div className="text-lg">{getFullName()}</div>
            <div className="text-sm text-white/80">ID: {user?._id?.slice(-8)}</div>
            <Badge className="mt-1 bg-white/20 text-white border-0 text-xs">
              <Star className="h-3 w-3 mr-1" />
              {user?.role === 'admin' ? 'Admin' : user?.role === 'marketing_manager' ? 'Manager' : user?.role === 'marketing' || user?.role === 'marketing_user' ? 'Marketing' : user?.subscription?.isActive ? 'Premium' : 'Free Plan'}
            </Badge>
          </div>
        </div>
      </div>
      
      <div className="p-4 space-y-1 overflow-y-auto flex-1">
        {/* Marketing Manager Panel Section - Only for Marketing Managers */}
        {isMarketingManager && (
          <>
            <div className="text-xs text-gray-500 px-3 py-2">MARKETING MANAGER</div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" 
              onClick={() => handleAction(() => onNavigate('marketing-manager'))}
            >
              <TrendingUp className="size-5 mr-3" />
              <div className="flex items-center gap-2">
                <span>Manager Panel</span>
                <Badge className="text-xs bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-0">
                  Manager
                </Badge>
              </div>
            </Button>
            <Separator className="my-2" />
          </>
        )}

        {/* Marketing Panel Section - Only for Marketing Users */}
        {isMarketing && (
          <>
            <div className="text-xs text-gray-500 px-3 py-2">MARKETING</div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-purple-600 hover:text-purple-700 hover:bg-purple-50" 
              onClick={() => handleAction(() => onNavigate('marketing-panel'))}
            >
              <TrendingUp className="size-5 mr-3" />
              <div className="flex items-center gap-2">
                <span>Marketing Panel</span>
                <Badge className="text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
                  Earn
                </Badge>
              </div>
            </Button>
            <Separator className="my-2" />
          </>
        )}

        <div className="text-xs text-gray-500 px-3 py-2">PROFILE</div>
        <Button 
          variant="ghost" 
          className="w-full justify-start" 
          onClick={() => handleAction(onProfile)}
        >
          <User className="size-5 mr-3" />
          My Profile
        </Button>
        <Button 
          variant="ghost" 
          className="w-full justify-start" 
          onClick={() => handleAction(() => onNavigate('edit-profile'))}
        >
          <User className="size-5 mr-3" />
          Edit Profile
        </Button>
        
        <Separator className="my-2" />
        
        <div className="text-xs text-gray-500 px-3 py-2">DISCOVER</div>
        {/* AI Search - Hidden */}
        {/* <Button 
          variant="ghost" 
          className="w-full justify-start" 
          onClick={() => handleAction(() => onNavigate('search'))}
        >
          <Search className="size-5 mr-3" />
          AI Search
        </Button> */}
        {/* Advanced Filters - Hidden */}
        {/* <Button 
          variant="ghost" 
          className="w-full justify-start" 
          onClick={() => handleAction(() => onNavigate('advanced-matching'))}
        >
          <Zap className="size-5 mr-3" />
          <div className="flex items-center gap-2">
            <span>Advanced Filters</span>
            <Badge variant="secondary" className="text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
              New
            </Badge>
          </div>
        </Button> */}
        {/* Community - Hidden */}
        {/* <Button 
          variant="ghost" 
          className="w-full justify-start" 
          onClick={() => handleAction(() => onNavigate('community'))}
        >
          <Globe className="size-5 mr-3" />
          Community
        </Button> */}
        {/* Blog - Hidden */}
        {/* <Button 
          variant="ghost" 
          className="w-full justify-start" 
          onClick={() => handleAction(() => onNavigate('blogs'))}
        >
          <BookOpen className="size-5 mr-3" />
          Blog
        </Button> */}
        <Button 
          variant="ghost" 
          className="w-full justify-start" 
          onClick={() => handleAction(() => onNavigate('kundli-maker'))}
        >
          <Moon className="size-5 mr-3" />
          <div className="flex items-center gap-2">
            <span>Kundli Maker</span>
            <Badge variant="secondary" className="text-xs bg-gradient-to-r from-orange-500 to-rose-500 text-white border-0">
              Free
            </Badge>
          </div>
        </Button>
        
        <Separator className="my-2" />
        
        <div className="text-xs text-gray-500 px-3 py-2">PREMIUM</div>
        {/* Upgrade to Premium - Hidden */}
        {/* <Button 
          variant="ghost" 
          className={`w-full justify-start ${currentTheme.primaryText}`}
          onClick={() => handleAction(() => onNavigate('premium-marketplace'))}
        >
          <Sparkles className="size-5 mr-3" />
          Upgrade to Premium
        </Button> */}
        {/* Premium Shop - Hidden */}
        {/* <Button 
          variant="ghost" 
          className="w-full justify-start"
          onClick={() => handleAction(() => onNavigate('premium-shop'))}
        >
          <Crown className="size-5 mr-3" />
          Premium Shop
        </Button> */}
        <Button 
          variant="ghost" 
          className="w-full justify-start"
          onClick={() => handleAction(() => onNavigate('refer-earn'))}
        >
          <Gift className="size-5 mr-3" />
          Refer & Earn
        </Button>
        
        {/* Invite via SMS - Only show in Expo app */}
        {(window as any).isNativeApp && settings?.features?.smsInviteEnabled === true && (
          <Button 
            variant="ghost" 
            className="w-full justify-start text-green-600 hover:text-green-700 hover:bg-green-50"
            onClick={() => {
              console.log('📲 [SMS Invite] ========== BUTTON CLICKED ==========');
              console.log('📲 [SMS Invite] isNativeApp:', (window as any).isNativeApp);
              console.log('📲 [SMS Invite] ReactNativeWebView exists:', !!(window as any).ReactNativeWebView);
              console.log('📲 [SMS Invite] User data:', {
                referralCode: user?.referralCode,
                fullName: user?.fullName,
                firstName: user?.firstName
              });
              
              // Check if running in React Native WebView
              if ((window as any).ReactNativeWebView) {
                console.log('📲 [SMS Invite] Running in WebView - using postMessage');
                
                const message = {
                  type: 'SHOW_SMS_INVITE',
                  referralCode: user?.referralCode || 'REFER',
                  userName: user?.fullName || user?.firstName || 'Your Friend'
                };
                
                console.log('📲 [SMS Invite] Sending message:', message);
                
                try {
                  // Send message to React Native
                  (window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
                  console.log('📲 [SMS Invite] ✅ Message sent successfully!');
                } catch (error) {
                  console.error('❌ [SMS Invite] Error sending message:', error);
                }
              } else {
                console.log('📲 [SMS Invite] Running in web - using showSmsInviteModal or event');
                
                // For web, try the global function first
                if (typeof (window as any).showSmsInviteModal === 'function') {
                  console.log('📲 [SMS Invite] Calling window.showSmsInviteModal()');
                  (window as any).showSmsInviteModal();
                } else {
                  console.log('📲 [SMS Invite] Dispatching custom event');
                  // Dispatch event as backup
                  const event = new CustomEvent('webview-message', {
                    detail: {
                      type: 'SHOW_SMS_INVITE',
                      referralCode: user?.referralCode || 'REFER',
                      userName: user?.fullName || user?.firstName || 'Your Friend'
                    }
                  });
                  window.dispatchEvent(event);
                  console.log('📲 [SMS Invite] Event dispatched');
                }
              }
              
              console.log('📲 [SMS Invite] Closing sidebar...');
              if (onClose) onClose();
              console.log('📲 [SMS Invite] ========== BUTTON CLICK END ==========');
            }}
          >
            <MessageCircle className="size-5 mr-3" />
            <div className="flex items-center gap-2">
              <span>Invite via SMS</span>
              <Badge className="text-xs bg-gradient-to-r from-green-500 to-emerald-500 text-white border-0">
                Earn
              </Badge>
            </div>
          </Button>
        )}
        
        <Separator className="my-2" />
        
        <div className="text-xs text-gray-500 px-3 py-2">SETTINGS</div>
        <Button 
          variant="ghost" 
          className="w-full justify-start" 
          onClick={() => handleAction(onSettings)}
        >
          <Settings className="size-5 mr-3" />
          Settings
        </Button>
        <Button 
          variant="ghost" 
          className="w-full justify-start" 
          onClick={() => handleAction(() => onNavigate('change-password'))}
        >
          <Lock className="size-5 mr-3" />
          Change Password
        </Button>
        
        {isAdmin && (
          <>
            <Separator className="my-2" />
            
            <div className="text-xs text-gray-500 px-3 py-2">ADMIN</div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-purple-600 hover:text-purple-700 hover:bg-purple-50" 
              onClick={() => handleAction(() => onNavigate('admin'))}
            >
              <ShieldCheck className="size-5 mr-3" />
              <div className="flex items-center gap-2">
                <span>Admin Panel</span>
                <Badge className="text-xs bg-gradient-to-r from-purple-500 to-indigo-600 text-white border-0">
                  Admin
                </Badge>
              </div>
            </Button>
          </>
        )}
        
        <Separator className="my-2" />
        
        <div className="text-xs text-gray-500 px-3 py-2">SUPPORT</div>
        <Button 
          variant="ghost" 
          className="w-full justify-start"
          onClick={() => handleAction(() => onNavigate('help'))}
        >
          <HelpCircle className="size-5 mr-3" />
          Help & Support
        </Button>
        
        <Separator className="my-2" />
        
        <Button 
          variant="ghost" 
          className="w-full justify-start text-rose-600 hover:text-rose-700 hover:bg-rose-50"
          onClick={onLogout}
        >
          <LogOut className="size-5 mr-3" />
          Logout
        </Button>
      </div>
    </div>
  );
}